<template>
  <TbLoading
    v-if="pageState === PageState.loading"
    class="p-8"
  />
  <div v-else-if="pageState === PageState.error">
    <ErrorComponent :error="pageError" />
  </div>
  <div v-else>
    <ReviewChanges
      v-if="showReviewScreen"
      :editing-schedule="editingSchedule"
      :change-result="changesResult"
      :submitting="submitting"
      :preview-error="loadPreviewError"
      :update-error="updateScheduleError"
      :summary="summary"
      @confirm="handleConfirm"
    />
    <EditPayments
      v-else
      :editing-schedule="editingSchedule"
      :change-result="changesResult"
      :original-schedule="originalSchedule"
      :error="loadPreviewError"
      :issues="loadPreviewIssues"
      :is-schedule-edited="isScheduleEdited"
      :review-url="reviewUrl"
      :order-id="orderId"
      :project-id="projectId"
      @schedule-change="handleScheduleChange"
      @reset="handleReset"
    />
  </div>
  <TbNotification
    :show="showSuccessToast"
    title="Success 🎉"
    message="Payment schedule updated successfully."
    card-class="text-white bg-success"
    @close="showSuccessToast = false"
  />
</template>

<script setup lang="ts">
import { TbLoading, TbNotification } from '@/components/tasty_bistro';
import { getErrorIssues } from '@/lib/http';
import { Pages } from '@/router';
import { PageState } from '@/types';
import { AxiosError } from 'axios';
import {
  computed, onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getSchedule } from './api/get_schedule';
import { getEditingSchedule, scheduleChangesToString, stringToScheduleChanges } from './api/helpers';
import { previewUpdatedSchedule } from './api/preview_updated_schedule';
import { updateSchedule } from './api/update_schedule';
import EditPayments from './components/edit_payments.vue';
import ErrorComponent from './components/error.vue';
import ReviewChanges from './components/review_changes.vue';
import {
  EditingPayment,
  IssueWithData,
  OrderPayment,
  ResultChangedInstallments, ScheduleChanges,
} from './types';

const route = useRoute();
const router = useRouter();
const projectId = route.params.projectId as string;
const orderId = route.params.id as string;
const changesString = computed(() => (route.query.changes as string) || '');
const scheduleStaticIdentifier = computed(() => {
  const { query } = route;
  return query.scheduleId as string;
});
const changes = ref<ScheduleChanges>(changesString.value ? stringToScheduleChanges(changesString.value) : {});
const changesResult = ref<ResultChangedInstallments>({});
const isScheduleEdited = computed(() => (Object.keys(changes.value).length > 0));
const showReviewScreen = computed(() => {
  const { query } = route;
  return query.review === '1';
});
const showSuccessToast = ref(false);
const pageError = ref<unknown>(null);
const loadPreviewError = ref<unknown>(null);
const updateScheduleError = ref<unknown>(null);
const pageState = ref(PageState.loading);
const originalSchedule = ref<OrderPayment[]>([]);
const editingSchedule = ref<EditingPayment[]>([]);
const fetchPreviewDebounceTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const summary = ref<string[]>([]);
const submitting = ref(false);
// TODO: This should be a computed value based on the error value for updating values
const loadPreviewIssues = ref<IssueWithData[]>([]);
const reviewUrl = computed(() => {
  const { path, query } = route;
  return {
    path,
    query: {
      ...query,
      review: '1',
    },
  };
});

watch(
  () => route.fullPath,
  (newPath, oldPath) => {
    const newUrlChanges = new URLSearchParams(newPath).get('changes');
    const oldUrlChanges = new URLSearchParams(oldPath).get('changes');
    // Don't do anything if the list of intended schedule changes isn't different
    if (newUrlChanges === oldUrlChanges) {
      return;
    }

    // Only try to load the preview if there are actual changes. The backend considers
    // it an error to preview changes when there aren't any
    changes.value = newUrlChanges ? stringToScheduleChanges(newUrlChanges) : {};
    editingSchedule.value = getEditingSchedule(originalSchedule.value, changes.value);
    changesResult.value = {};

    // This timeout is so that if you undo/redo really quickly we don't send a ton of requests
    // if we don't need to
    if (fetchPreviewDebounceTimeout.value !== null) {
      clearTimeout(fetchPreviewDebounceTimeout.value);
    }
    if (Object.keys(changes.value).length > 0) {
      fetchPreviewDebounceTimeout.value = setTimeout(() => {
        loadPreview();
      }, 250);
    } else {
      summary.value = [];
      loadPreviewError.value = null;
      loadPreviewIssues.value = [];
    }
  },
);

function updateUrl(scheduleStaticIdentifier: string, changes: ScheduleChanges) {
  const query: Record<string, string> = {};
  if (scheduleStaticIdentifier) {
    query.scheduleId = scheduleStaticIdentifier;
  }
  if (changes && Object.keys(changes).length > 0) {
    query.changes = scheduleChangesToString(originalSchedule.value, changes);
  }
  if (query.changes === '') {
    delete query.changes;
  }
  router.push({
    query,
  });
}

function undo() {
  router.back();
}

function redo() {
  router.forward();
}

const handleKeyDown = (e: KeyboardEvent) => {
  // If they are focus in a text box then let undo work like normal
  if (e.target instanceof HTMLInputElement) {
    return;
  }

  const zKey = e.key === 'z' || e.key === 'Z';
  const yKey = e.key === 'y' || e.key === 'Y';
  const isMeta = e.metaKey || e.ctrlKey;

  if (!isMeta) {
    return;
  }

  // cmd+y and cmd+shift+z will both redo
  const isRedo = (e.shiftKey && zKey) || yKey;
  if (isRedo) {
    e.preventDefault();
    redo();
    return;
  }

  const isUndo = zKey;
  if (isUndo) {
    e.preventDefault();
    undo();
  }
};

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown);
});

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

const loadOriginalSchedule = async (): Promise<void> => {
  pageState.value = PageState.loading;
  pageError.value = null;
  try {
    const data = await getSchedule({ projectId, orderId });
    if (!data.scheduleStaticIdentifier) {
      throw new Error('Schedule static identifier not returned');
    }

    // If the schedule identifier has changed, we do a full page reload
    // This is done out of paranoia that things will be weirdly out of sync if we don't
    // Maybe we don't need to do this.
    let fixUrl = false;
    if (data.scheduleStaticIdentifier !== scheduleStaticIdentifier.value) {
      fixUrl = true;
    }

    // This handles the case where there's like "a:1000" in the URL (maybe from a past edit) but the value
    // from the API is also 1000. In that case we want to remove that change from the URL
    const newChangesString = scheduleChangesToString(data.schedule, changes.value);
    if (changesString.value !== newChangesString) {
      fixUrl = true;
      console.error('fix url cause', changesString.value, '!=', newChangesString);
    }

    if (fixUrl) {
      const query = new URLSearchParams({
        scheduleId: data.scheduleStaticIdentifier,
      });
      if (newChangesString) {
        query.set('changes', newChangesString);
      }
      window.location.href = `${window.location.pathname}?${query.toString()}`;
      return;
    }

    originalSchedule.value = data.schedule;
    editingSchedule.value = getEditingSchedule(originalSchedule.value, changes.value);
    pageState.value = PageState.loaded;
  } catch (ex) {
    pageError.value = ex;
    pageState.value = PageState.error;
  }
};

// This is used so that if 3 requests go out very quickly, but then come back in a different order, we don't
// end up showing the result from request #2 just because it came back after the response of request #3
const loadPreviewAbortController = ref<AbortController | null>(null);

const loadPreview = async (): Promise<void> => {
  // This cancels the previous request if it's still running
  if (loadPreviewAbortController.value) {
    loadPreviewAbortController.value.abort();
  }
  loadPreviewAbortController.value = new AbortController();
  loadPreviewError.value = null;
  loadPreviewIssues.value = [];
  summary.value = [];

  try {
    const data = await previewUpdatedSchedule({
      orderId,
      projectId,
      scheduleStaticIdentifier: scheduleStaticIdentifier.value,
      changes: changes.value,
      originalSchedule: originalSchedule.value,
    }, loadPreviewAbortController.value?.signal);
    editingSchedule.value = getEditingSchedule(data.schedule, changes.value, data.installmentChanges);
    summary.value = data.summary;
    changesResult.value = data.installmentChanges;
  } catch (ex) {
    if (ex instanceof AxiosError && ex.code === 'ERR_CANCELED') {
      console.error('ignoring canceled error');
      return;
    }
    loadPreviewError.value = ex;
    loadPreviewIssues.value = getErrorIssues(ex);
    editingSchedule.value = getEditingSchedule(originalSchedule.value, changes.value);
  }
};

onMounted(async () => {
  await loadOriginalSchedule();
  if (changesString.value) {
    await loadPreview();
  } else {
    loadPreviewIssues.value = [];
  }
});

const handleScheduleChange = async (
  installmentId: string,
  field: 'newAmountInCents' | 'newDueDate',
  value: string | number,
) => {
  const originalItem = originalSchedule.value.find((item) => item.installmentId === installmentId);
  const newChanges: ScheduleChanges = JSON.parse(JSON.stringify(changes.value));
  newChanges[installmentId] = newChanges[installmentId] || {} as ScheduleChanges[string];
  if (field === 'newAmountInCents') {
    const newAmountInCents = `${value}`;
    const originalAmountInCents = `${originalItem?.amountInCents}`;
    if (originalAmountInCents === newAmountInCents) {
      delete newChanges[installmentId].newAmountInCents;
    } else {
      newChanges[installmentId].newAmountInCents = Number(value);
    }
  }
  if (field === 'newDueDate') {
    const newDueDate = value as string;
    if (originalItem?.dueDate === newDueDate) {
      delete newChanges[installmentId].newDueDate;
    } else {
      newChanges[installmentId].newDueDate = newDueDate;
    }
  }

  updateUrl(scheduleStaticIdentifier.value, newChanges);
};

const handleReset = async () => {
  updateUrl(scheduleStaticIdentifier.value, {});
};

const handleConfirm = async () => {
  if (submitting.value) {
    return;
  }
  submitting.value = true;
  updateScheduleError.value = null;

  try {
    await updateSchedule({
      projectId,
      orderId,
      scheduleStaticIdentifier: scheduleStaticIdentifier.value,
      changes: changes.value,
      originalSchedule: originalSchedule.value,
    });

    showSuccessToast.value = true;

    // Show toast for 1.5 seconds before redirect
    setTimeout(() => {
      router.push({
        name: Pages.dashboardOrders,
      });
    }, 1500);
  } catch (error) {
    updateScheduleError.value = error;
  } finally {
    submitting.value = false;
  }
};
</script>
